import { BnplLimitsApplicationSlugEnum } from '.spec/self-care/tsTypes';
import React from 'react';

import { MyProfile } from '@skippay/core/@types/myProfile';
import Loader from '@skippay/core/components/Loader';
import Redirect from '@skippay/core/components/Redirect';

import { OVERVIEW } from 'constants/routes';

import { useBnplLimitManagement } from './hooks/useBnplLimitManagement';
import AmlPepRequired from './views/AmlPepRequired';
import KycDataConfirmation from './views/KycDataConfirmation';
import KycRequired from './views/KycRequired';
import ProductSelection from './views/ProductSelection';
import Rejected from './views/Rejected';
import Success from './views/Success';

interface Props {
	phoneNumbers?: MyProfile['phoneNumbers'];
}

function BnplLimitManagement({ phoneNumbers }: Props): JSX.Element {
	const {
		loading,
		application,
		isSubmitting,
		onConfirmProducts,
		onConfirmKycData,
		onDeclineKycData,
	} = useBnplLimitManagement();

	if (loading) {
		return <Loader fullSize />;
	}

	if (!application) {
		return <Redirect to={OVERVIEW} />;
	}

	switch (application.slug) {
		case BnplLimitsApplicationSlugEnum.INITIAL: {
			const pendingProducts = (application.products ?? []).filter(
				(product) => !product.isConfirmed
			);

			if (pendingProducts.length === 0) {
				return <Redirect to={OVERVIEW} />;
			}

			return (
				<ProductSelection
					products={pendingProducts}
					isConfirming={isSubmitting}
					onConfirm={onConfirmProducts}
				/>
			);
		}

		case BnplLimitsApplicationSlugEnum.KYC_DATA_CONFIRMATION:
			return (
				<KycDataConfirmation
					kycData={application.singleKycData?.customerData}
					phoneNumbers={phoneNumbers}
					isConfirming={isSubmitting}
					onConfirm={onConfirmKycData}
					onDecline={onDeclineKycData}
				/>
			);

		case BnplLimitsApplicationSlugEnum.BNPL_PRE_APPROVED_KYC:
		case BnplLimitsApplicationSlugEnum.BNPL_PRE_APPROVED_AML:
			return <KycRequired applicationId={application.id} />;

		case BnplLimitsApplicationSlugEnum.SUCCESS:
			return <Success />;

		case BnplLimitsApplicationSlugEnum.REJECTED:
			return <Rejected />;

		case BnplLimitsApplicationSlugEnum.AML_PEP_REQUIRED:
			return <AmlPepRequired />;

		default:
			throw new Error(`Unknown BNPL limit management slug: ${application.slug}`);
	}
}

export default BnplLimitManagement;
